import { useBookingTrips } from '@/hooks/useBookingTrips';
import type {
  AdmissionDTO,
  BookingDTO,
  FeeDTO,
  NonTripOfferDTO,
  PendingVoucherDTO,
  TripOfferDTO,
} from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import type { TravelAccountsMap } from '@/types/travelpass';
import type { CheckoutValues } from '@/utils/zodSchema';
import { useCallback, useMemo } from 'react';

export interface BookingSummaryData {
  outboundTrips: NonNullable<BookingDTO['bookedTrips']>;
  inboundTrips: NonNullable<BookingDTO['bookedTrips']>;
  nonTripOffers: NonNullable<BookingDTO['nonTripOffers']>;
  fees: Array<FeeDTO>;
  vouchers: Array<PendingVoucherDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
  travelAccountsMap: TravelAccountsMap | undefined;
  reservationFeesMap: Record<NonNullable<FeeDTO['id']>, FeeDTO>;
  refundTotal: number;
  displayPrice: BookingDTO['provisionalPrice'] | BookingDTO['confirmedPrice'];
}

const useBookingSummaryData = ({
  booking,
  passengers,
  vouchers = [],
  displayPrice,
  travelAccountsMap,
  allowedStatuses,
}: {
  booking: BookingDTO | null;
  passengers: CheckoutValues['passengers'] | BookingDTO['passengers'];
  vouchers: BookingDTO['pendingVouchers'];
  displayPrice: BookingDTO['provisionalPrice'] | BookingDTO['confirmedPrice'];
  travelAccountsMap: TravelAccountsMap | undefined;
  allowedStatuses: Array<AdmissionDTO['status']>;
}): BookingSummaryData => {
  const { outboundTrips, inboundTrips } = useBookingTrips(booking);

  const findTripsWithAllowedStatuses = useCallback(
    (trips: NonNullable<BookingDTO['bookedTrips']>) =>
      trips.map((trip) => ({
        ...trip,
        bookedOffers: trip.bookedOffers?.map((offer) => ({
          ...offer,
          admissions: offer.admissions?.filter((admission) =>
            allowedStatuses.includes(admission.status)
          ),
        })),
      })),
    [allowedStatuses]
  );

  const findNonTripsWithAllowedStatuses = useCallback(
    (nonTrips: BookingDTO['nonTripOffers']) =>
      (nonTrips ?? []).map((nonTrip) => ({
        ...nonTrip,
        admissions: nonTrip.admissions?.filter((admission) =>
          allowedStatuses.includes(admission.status)
        ),
      })),
    [allowedStatuses]
  );

  const getFees = useCallback(
    (
      feeRefs: string[] | undefined,
      offerFees: TripOfferDTO['fees'] | NonTripOfferDTO['fees'],
      allowedStatuses?: NonNullable<FeeDTO['status']>
    ) =>
      (feeRefs ?? []).flatMap(
        (feeRef) =>
          (offerFees ?? []).find(
            (fee) =>
              fee.id === feeRef &&
              (fee.status && allowedStatuses
                ? allowedStatuses.includes(fee.status)
                : true)
          ) ?? []
      ),
    []
  );

  const getAdmissionFees = useCallback(
    ({
      admission,
      offerFees,
      allowedFeeRefSources,
      allowedStatuses,
    }: {
      admission: AdmissionDTO;
      offerFees: TripOfferDTO['fees'] | NonTripOfferDTO['fees'];
      allowedFeeRefSources: Array<
        'admission' | 'admission.ancillaries' | 'admission.reservations'
      >;
      allowedStatuses?: NonNullable<FeeDTO['status']>;
    }) => {
      const allFees = [];

      if (allowedFeeRefSources.includes('admission')) {
        const admissionFees = getFees(
          admission.feeRefs,
          offerFees,
          allowedStatuses
        );

        allFees.push(...admissionFees);
      }

      if (allowedFeeRefSources.includes('admission.ancillaries')) {
        const admissionAncillariesFees = (admission.ancillaries ?? []).flatMap(
          (ancillary) => getFees(ancillary.feeRefs, offerFees, allowedStatuses)
        );

        allFees.push(...admissionAncillariesFees);
      }

      if (allowedFeeRefSources.includes('admission.reservations')) {
        const admissionReservationsFees = (
          admission.reservations ?? []
        ).flatMap((reservation) =>
          getFees(reservation.feeRefs, offerFees, allowedStatuses)
        );

        allFees.push(...admissionReservationsFees);
      }

      return allFees;
    },
    [getFees]
  );

  const fees = useMemo(
    () => [
      ...(booking?.bookedTrips ?? []).flatMap((trip) =>
        (trip.bookedOffers ?? []).flatMap((offer) =>
          (offer.admissions ?? []).flatMap((admission) =>
            getAdmissionFees({
              admission,
              offerFees: offer.fees,
              allowedFeeRefSources: ['admission', 'admission.ancillaries'],
            })
          )
        )
      ),
      ...(booking?.nonTripOffers ?? []).flatMap((offer) =>
        (offer.admissions ?? []).flatMap((admission) =>
          getAdmissionFees({
            admission,
            offerFees: offer.fees,
            allowedFeeRefSources: ['admission', 'admission.ancillaries'],
          })
        )
      ),
    ],
    [booking?.bookedTrips, booking?.nonTripOffers, getAdmissionFees]
  );

  const passengersMap = useMemo(
    () =>
      (passengers ?? []).reduce<CheckoutOrBookingPassengerMap>(
        (acc, current) => ({
          ...acc,
          ...(current.id && { [current.id]: current }),
        }),
        {}
      ),
    [passengers]
  );

  const reservationFeesMap = useMemo(() => {
    const reservationFees = [
      ...(booking?.bookedTrips ?? []).flatMap((trip) =>
        (trip.bookedOffers ?? []).flatMap((offer) =>
          (offer.admissions ?? []).flatMap((admission) =>
            getAdmissionFees({
              admission,
              offerFees: offer.fees,
              allowedFeeRefSources: ['admission.reservations'],
            })
          )
        )
      ),
      ...(booking?.nonTripOffers ?? []).flatMap((offer) =>
        (offer.admissions ?? []).flatMap((admission) =>
          getAdmissionFees({
            admission,
            offerFees: offer.fees,
            allowedFeeRefSources: ['admission.reservations'],
          })
        )
      ),
    ];

    return reservationFees.reduce<Record<NonNullable<FeeDTO['id']>, FeeDTO>>(
      (acc, current) => ({
        ...acc,
        ...(current.id && { [current.id]: current }),
      }),
      {}
    );
  }, [booking?.bookedTrips, booking?.nonTripOffers, getAdmissionFees]);

  const refundTotal = useMemo(() => {
    const refundedTripAdmissions = (booking?.bookedTrips ?? []).flatMap(
      (trip) =>
        (trip.bookedOffers ?? []).flatMap((offer) =>
          (offer.admissions ?? []).map((admission) => {
            const admissionAmount =
              admission.status === 'REFUNDED'
                ? admission.price?.amount || 0
                : 0;
            const reservationsTotal = (admission.reservations || [])
              .filter((reservation) => reservation.status === 'REFUNDED')
              .reduce((acc, current) => acc + (current.price?.amount || 0), 0);
            const ancillariesTotal = (admission.ancillaries ?? [])
              .filter((ancillary) => ancillary.status === 'REFUNDED')
              .reduce((acc, current) => acc + (current.price?.amount || 0), 0);
            const feesTotal = getAdmissionFees({
              admission,
              offerFees: offer.fees,
              allowedFeeRefSources: [
                'admission',
                'admission.ancillaries',
                'admission.reservations',
              ],
              allowedStatuses: 'RETURN_FEE',
            }).reduce((acc, current) => acc + (current.price?.amount || 0), 0);

            return [
              admissionAmount + reservationsTotal + ancillariesTotal,
              feesTotal,
            ];
          })
        )
    );
    const refundedNonTripAdmissions = (booking?.nonTripOffers ?? []).flatMap(
      (offer) =>
        (offer.admissions ?? []).map((admission) => {
          const admissionAmount =
            admission.status === 'REFUNDED' ? admission.price?.amount || 0 : 0;
          const reservationsTotal = (admission.reservations || [])
            .filter((reservation) => reservation.status === 'REFUNDED')
            .reduce((acc, current) => acc + (current.price?.amount || 0), 0);
          const feesTotal = getAdmissionFees({
            admission,
            offerFees: offer.fees,
            allowedFeeRefSources: [
              'admission',
              'admission.ancillaries',
              'admission.reservations',
            ],
            allowedStatuses: 'RETURN_FEE',
          }).reduce((acc, current) => acc + (current.price?.amount || 0), 0);

          return [admissionAmount + reservationsTotal, feesTotal];
        })
    );
    const [refundedAdmissionTotal, refundedFeesTotal] = [
      ...refundedTripAdmissions,
      ...refundedNonTripAdmissions,
    ].reduce<[number, number]>(
      ([sumAdmission, sumFee], [admission, fee]) => [
        sumAdmission + (admission || 0),
        sumFee + (fee || 0),
      ],
      [0, 0]
    );

    return refundedAdmissionTotal - refundedFeesTotal;
  }, [booking?.bookedTrips, booking?.nonTripOffers, getAdmissionFees]);

  return {
    outboundTrips: findTripsWithAllowedStatuses(outboundTrips),
    inboundTrips: findTripsWithAllowedStatuses(inboundTrips),
    nonTripOffers: findNonTripsWithAllowedStatuses(booking?.nonTripOffers),
    fees,
    vouchers,
    passengersMap,
    travelAccountsMap,
    reservationFeesMap,
    refundTotal,
    displayPrice,
  };
};

export default useBookingSummaryData;
